import React, { useMemo, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  Checkbox,
  ListItemText,
  Collapse,
  Box
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

export default function BulkScheduleDialog({
  open,
  options,
  selected,
  onChange,
  onClose,
  onNext
}) {
  // filter out already‐scheduled
  const filteredOptions = useMemo(
    () =>
      options.filter(item => {
        const jobs = item.jobs ?? []
        if (jobs.length === 0) return true
        return !jobs.some(j => j.occurrenceNo > 0)
      }),
    [options]
  )

  // group by bundleID
  const { groups, noBundle } = useMemo(() => {
    const g = {}
    const none = []
    filteredOptions.forEach(item => {
      if (item.bundleID) {
        if (!g[item.bundleID]) {
          g[item.bundleID] = { bundleName: item.bundleName, items: [] }
        }
        g[item.bundleID].items.push(item)
      } else {
        none.push(item)
      }
    })
    return { groups: Object.values(g), noBundle: none }
  }, [filteredOptions])

  const isSelected = workReqID =>
    selected.some(s => s.workReqID === workReqID)

  const handleToggleItem = item => {
    if (isSelected(item.workReqID)) {
      onChange(selected.filter(s => s.workReqID !== item.workReqID))
    } else {
      onChange([...selected, item])
    }
  }

  const handleToggleGroup = group => {
    const allIds = group.items.map(i => i.workReqID)
    const currently = selected.map(s => s.workReqID)
    const allSelected = allIds.every(id => currently.includes(id))

    if (allSelected) {
      onChange(selected.filter(s => !allIds.includes(s.workReqID)))
    } else {
      const toAdd = group.items.filter(i => !currently.includes(i.workReqID))
      onChange([...selected, ...toAdd])
    }
  }

  // which bundles are open?
  const [openBundles, setOpenBundles] = useState({})
  const toggleBundle = name =>
    setOpenBundles(prev => ({ ...prev, [name]: !prev[name] }))

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>Select services to schedule</DialogTitle>
      <DialogContent dividers>
        <List>
          {groups.map(group => {
            const allIds = group.items.map(i => i.workReqID)
            const currently = selected.map(s => s.workReqID)
            const allSelected = allIds.every(id => currently.includes(id))
            const someSelected =
              !allSelected && allIds.some(id => currently.includes(id))
            const isOpen = !!openBundles[group.bundleName]

            return (
              <React.Fragment key={group.bundleName}>
                <ListItem>
                  {/* expand/collapse arrow */}
                  <ListItemIcon
                    sx={{ minWidth: 32, cursor: 'pointer' }}
                    onClick={() => toggleBundle(group.bundleName)}
                  >
                    {isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                  </ListItemIcon>

                  {/* bundle checkbox */}
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Checkbox
                      edge="start"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onClick={e => {
                        e.stopPropagation()
                        handleToggleGroup(group)
                      }}
                    />
                  </ListItemIcon>

                  {/* bundle label */}
                  <ListItemText primary={group.bundleName} />
                </ListItem>

                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {group.items.map(item => (
                      <ListItem
                        key={item.workReqID}
                        sx={{ pl: 6 }}
                        button
                        onClick={() => handleToggleItem(item)}
                      >
                        <ListItemIcon>
                          <Checkbox edge="start" checked={isSelected(item.workReqID)} />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${item.industryName} – ${item.subServiceName}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            )
          })}

          {noBundle.map(item => (
            <ListItem
              key={item.workReqID}
              button
              onClick={() => handleToggleItem(item)}
            >
              <ListItemIcon>
                <Checkbox edge="start" checked={isSelected(item.workReqID)} />
              </ListItemIcon>
              <ListItemText
                primary={`${item.industryName} – ${item.subServiceName}`}
              />
            </ListItem>
          ))}

          {filteredOptions.length === 0 && (
            <ListItem>
              <ListItemText
                primary="No services available for bulk scheduling"
                secondary="All selected services already have scheduled occurrences."
              />
            </ListItem>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="info" onClick={onClose}>Cancel</Button>
        <Button  variant="contained" color="main" onClick={onNext} disabled={selected.length === 0}>
          Next
        </Button>
      </DialogActions>
    </Dialog>
  )
}
